import { useState } from "react";
import { useDispatch } from "react-redux";
import { StepDefinition } from "../../../../../../../model/workflows/step-definition";
import { ActionDefinition } from "../../../../../../../model/workflows/action-definition";
import { SelectItem } from "../../../../../../../common/components/new/select/Select";
import CommonCheckBox from "../../../../../../../common/components/checkbox/CommonCheckBox";
import Select from "../../../../../../../common/components/new/select/Select";
import Button from "../../../../../../../common/components/xepp/button/Button";
import { PenIcon, DeleteIcon, PlusIcon } from "../../../../../../../assets/icons";
import {
	addActionToStep,
	removeActionFromStep,
	updateActionInStep,
	updateActionNextStep,
	updateStepPaymentRequired,
	updateStepSLADurationType,
	updateStepSLADuration,
	updateStepAssignType,
	updateStepAbleToSeeCustomerDetails,
	updateStepAbleToSeeOtherStepInformation,
	updateStepAdditionalServiceRequest,
} from "../../../../../../../stores/slices/workflow-slice";
import AddNewActionModal from "../../../modals/add-new-action-modal/AddNewActionModal";
import styles from "./StepConfigurationPanel.module.scss";
import RadioButton from "../../../../../../../common/components/new/radio-button-area/radio-button/RadioButton";
import RadioButtonArea from "../../../../../../../common/components/new/radio-button-area/RadioButtonArea";
import RadioButtonField from "../../../../../../../common/components/radio-button-field/RadioButtonField";
import NumberInputWithButtons from "../../../../../../../common/components/xepp/number-input-with-buttons/NumberInputWithButtons";
import { SLADurationType } from "../../../../../../../model/slaDurationType";
import { AssignType } from "../../../../../../../model/workflows/asign-type";
import ServiceTypeFormInformation from "../../../../service-requests/components/FormInfo/ServiceTypeFormInformation";
import { v4 as uuidv4 } from "uuid";
import { finishAction } from "../../CustomStepsPage";

interface StepConfigurationPanelProps {
	step: StepDefinition | null;
	availableSteps: SelectItem[];
	onCancel: () => void;
	onCreateStep: () => void;
}

const StepConfigurationPanel = ({ step, availableSteps, onCancel, onCreateStep }: StepConfigurationPanelProps) => {
	const dispatch = useDispatch();
	const [isAddActionModalOpen, setIsAddActionModalOpen] = useState(false);
	const [editingAction, setEditingAction] = useState<ActionDefinition | null>(null);

	const assignmentTypeItems = [
		new SelectItem(AssignType.Automatic, "Automatic"),
		new SelectItem(AssignType.Supervisor, "Supervisor"),
		new SelectItem(AssignType.Claimable, "Claimable"),
	];
	const handleAddAction = (actionName: string) => {
		if (!step) return;

		if (editingAction) {
			// Update existing action
			const updatedAction: ActionDefinition = {
				...editingAction,
				name: actionName,
			};
			dispatch(updateActionInStep({ stepId: step.id, action: updatedAction }));
			setEditingAction(null);
		} else {
			// Create new action
			const newAction: ActionDefinition = {
				id: uuidv4(),
				name: actionName,
				stepDefinitionId: step.id,
				nextStepDefinitionId: null,
			};
			dispatch(addActionToStep({ stepId: step.id, action: newAction }));
		}
		setIsAddActionModalOpen(false);
	};

	const handleEditAction = (action: ActionDefinition) => {
		setEditingAction(action);
		setIsAddActionModalOpen(true);
	};

	const handleDeleteAction = (actionId: string) => {
		if (!step) return;
		dispatch(removeActionFromStep({ stepId: step.id, actionId }));
	};

	const handleActionNextStepChange = (actionId: string, nextStepId: string | null) => {
		if (!step) return;
		dispatch(updateActionNextStep({ stepId: step.id, actionId, nextStepId }));
	};

	const handleAssignTypeChange = (assignType: AssignType) => {
		if (!step) return;
		dispatch(updateStepAssignType({ stepId: step.id, assignType }));
	};

	if (!step) {
		return (
			<div className={styles.configurationPanel}>
				<div className={styles.noStepSelected}>Select a step to configure</div>
				<div className={styles.footerWrapper}>
					<Button text="Cancel Setup" onClick={onCancel} variant="outline" />
					<Button text="Create Step" onClick={onCreateStep} variant="secondary" />
				</div>
			</div>
		);
	}

	return (
		<div className={styles.configurationPanel}>
			{/* Define What Assignee Can Do at This Stage */}
			<div className={styles.configSection}>
				<div className={styles.sectionTitle}>Define What Assignee Can Do at This Stage</div>
				<div className={styles.checkboxGroup}>
					<CommonCheckBox
						title="View Details Customer Provided"
						color="black"
						selected={step.ableToSeeCustomerDetails}
						followState={false}
						onToggle={() => {
							dispatch(updateStepAbleToSeeCustomerDetails({ stepId: step.id, ableToSeeCustomerDetails: !step.ableToSeeCustomerDetails }));
						}}
					/>
					<div className={styles.checkboxWithFields}>
						<CommonCheckBox
							title="Collect Information from Customer"
							color="black"
							followState={false}
							selected={step.additionalServiceRequest !== null}
							onToggle={() => {
								var serviceRequestId = uuidv4();
								dispatch(
									updateStepAdditionalServiceRequest({
										stepId: step.id,
										additionalServiceRequest:
											step.additionalServiceRequest !== null
												? null
												: {
														id: serviceRequestId,
														name: ``,
														description: "",
														sectionDefinitions: [
															{
																id: uuidv4(),
																isDeleted: false,
																name: "Section 1",
																displayOrder: 1,
																attributeDefinitions: [],
																ServiceRequestId: serviceRequestId,
															},
														],
														isPaymentRequired: false,
														isPaymentPerSubtype: false,
														isDeleted: false,
														isPublished: false,
													},
									})
								);
							}}
						/>

						{step.additionalServiceRequest !== null && <ServiceTypeFormInformation step={step} />}
					</div>
					<CommonCheckBox
						title="View Information Provided in Other Steps"
						color="black"
						followState={false}
						selected={step.ableToSeeOtherStepInformation}
						onToggle={() => {
							dispatch(updateStepAbleToSeeOtherStepInformation({ stepId: step.id, ableToSeeOtherStepInformation: !step.ableToSeeOtherStepInformation }));
						}}
					/>
				</div>
			</div>

			{/* Define Action For This Stage */}
			<div className={styles.configSection}>
				<div className={styles.sectionTitle}>Define Action For This Stage</div>
				<div className={styles.actionsList}>
					{step.actions?.map(action => (
						<div key={action.id} className={styles.actionItem}>
							{action.name}
							<div className={styles.actionButtons}>
								<Button
									className={styles.iconButton}
									onClick={e => {
										e.stopPropagation();
										handleEditAction(action);
									}}
									text=""
									variant="outline"
									leftIcon={<PenIcon />}
								/>
								<Button
									className={styles.iconButton}
									onClick={e => {
										e.stopPropagation();
										handleDeleteAction(action.id);
									}}
									text=""
									variant="outline"
									leftIcon={<DeleteIcon />}
								/>
							</div>
						</div>
					))}
					<Button
						text="Add New Action"
						variant="outline"
						leftIcon={<PlusIcon />}
						onClick={() => {
							setEditingAction(null);
							setIsAddActionModalOpen(true);
						}}
					/>
				</div>
			</div>

			{/* Define Workflow For Action */}
			{step.actions && step.actions.length > 0 && (
				<div className={styles.configSection}>
					<div className={styles.sectionTitle}>Define Workflow For Action</div>
					<div className={styles.workflowDropdowns}>
						{step.actions.map(action => {
							let selectedItem = action.nextStepDefinitionId ? availableSteps.find(item => item.key === action.nextStepDefinitionId) : finishAction;

							return (
								<div key={action.id} className={styles.dropdownWrapper}>
									<Select
										label={`If ${action.name} Move to`}
										placeholder="Select Next Step"
										items={availableSteps}
										selected={selectedItem}
										onChange={(item: SelectItem | undefined) => {
											handleActionNextStepChange(action.id, item?.key || null);
										}}
									/>
								</div>
							);
						})}
					</div>
				</div>
			)}

			<div className={styles.configSection}>
				<div className={styles.sectionTitle}>Define If Payment Would Be Made In This Stage</div>
				<div className={styles.checkboxGroup}>
					<RadioButtonField
						text="Yes"
						initialValue={true}
						selectedValue={step.isPayRequired}
						color="#062A3A"
						onChangeValue={() => {
							dispatch(updateStepPaymentRequired({ stepId: step.id, paymentRequired: true }));
						}}
					/>
					<RadioButtonField
						text="No"
						initialValue={false}
						selectedValue={step.isPayRequired}
						color="#062A3A"
						onChangeValue={() => {
							dispatch(updateStepPaymentRequired({ stepId: step.id, paymentRequired: false }));
						}}
					/>
				</div>
			</div>
			<div className={styles.configSection}>
				<div className={styles.sectionTitle}>Define SLA Type</div>
				<div className={styles.checkboxGroup}>
					<div className={styles.checkboxWithInputField}>
						<RadioButtonField
							text="Hours"
							initialValue={SLADurationType.Hours}
							selectedValue={step.slaDurationType}
							color="#062A3A"
							onChangeValue={() => {
								dispatch(updateStepSLADurationType({ stepId: step.id, slaDurationType: SLADurationType.Hours }));
							}}
						/>
						<NumberInputWithButtons
							value={step.slaDuration}
							disabled={step.slaDurationType !== SLADurationType.Hours}
							showButtons={true}
							showClearButton={false}
							onNumberChange={(val: number) => {
								dispatch(updateStepSLADuration({ stepId: step.id, slaDuration: val }));
							}}
							stepSize={1}
							unit="Hours"
						/>
					</div>
					<div className={styles.checkboxWithInputField}>
						<RadioButtonField
							text="Days"
							initialValue={SLADurationType.Days}
							selectedValue={step.slaDurationType}
							color="#062A3A"
							onChangeValue={() => {
								dispatch(updateStepSLADurationType({ stepId: step.id, slaDurationType: SLADurationType.Days }));
							}}
						/>
						<NumberInputWithButtons
							value={step.slaDuration}
							disabled={step.slaDurationType !== SLADurationType.Days}
							unit="Days"
							showButtons={true}
							showClearButton={false}
							onNumberChange={(val: number) => {
								dispatch(updateStepSLADuration({ stepId: step.id, slaDuration: val }));
							}}
							stepSize={1}
						/>
					</div>
				</div>
			</div>
			<div className={styles.configSection}>
				<div className={styles.sectionTitle}>Assignment Type</div>
				<div className={styles.checkboxGroup}>
					<div className={styles.dropdownWrapper}>
						<Select
							label="Select Assignment Type"
							placeholder="Select Assignment Type"
							items={assignmentTypeItems}
							selected={assignmentTypeItems.find(item => item.key === step.assignType) || undefined}
							onChange={(item: SelectItem | undefined) => {
								if (item?.key !== undefined) {
									handleAssignTypeChange(item.key as AssignType);
								}
							}}
						/>
					</div>
				</div>
			</div>
			{/* Footer */}
			<div className={styles.footerWrapper}>
				<Button text="Create Next Step" onClick={onCreateStep} variant="secondary" />
			</div>

			{/* Add/Edit Action Modal */}
			<AddNewActionModal
				isOpen={isAddActionModalOpen}
				onClose={() => {
					setIsAddActionModalOpen(false);
					setEditingAction(null);
				}}
				onSave={handleAddAction}
				editingActionName={editingAction?.name}
			/>
		</div>
	);
};

export default StepConfigurationPanel;
